import { useContext } from 'react'

import {
  Button, Form, Line, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgencyPaymentMethods from '@hooks/useAgencyPaymentMethods'
import useCampaignQuote, { useCampaignQuoteForm } from '@hooks/useCampaignQuote'
import useSelector from '@hooks/useSelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditCampaignQuoteModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuote: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuote: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignQuoteModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignQuote, updateCampaignQuote } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initCampaignQuote = digObject(modalPayload, 'campaignQuote', {})

  const newRecord = !initCampaignQuote.id

  const {
    campaignQuote: {
      agency_id,
    },
    callbacks: {
      createCampaignQuote: createFn,
      updateCampaignQuote: updateFn,
    },
    creating,
    updating,
  } = useCampaignQuote(initCampaignQuote)

  const agencyPaymentMethodPayload = useAgencyPaymentMethods({
    filters: { agency_id },
    performHttpRequests: !!agency_id,
  })

  const {
    filteredAgencyPaymentMethods,
  } = agencyPaymentMethodPayload

  const paymentMethods = useSelector(reduxState => reduxState.entities.paymentMethods)

  const {
    entityState,
    entityState: {
      agency_payment_method_id,
      recipient_address,
      recipient_email,
      recipient_name,
      recipient_phone,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCampaignQuoteForm(initCampaignQuote, { validateOn: 'change' })

  const createOrUpdateCampaignQuotePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Campaign Quote ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Quote"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Recipient Address" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="recipient_address"
              style={{ height: 80, marginBottom: 0, resize: 'vertical' }}
              value={recipient_address}
              {...handlers}
            />
          </Form.Field>

          <Line />

          <Form.Field label="* Quote Contact (Vendor)">
            <input name="recipient_name" type="text" value={recipient_name} {...handlers} />
          </Form.Field>

          <Form.Field
            label="* Phone Number"
            marginTop="large"
          >
            <input name="recipient_phone" type="text" value={recipient_phone} {...handlers} />
          </Form.Field>

          <Form.Field
            label="* Email Address"
            marginTop="large"
          >
            <input name="recipient_email" type="email" value={recipient_email} {...handlers} />
          </Form.Field>

          <Line />

          <Form.Field
            label="Payment Method"
          >
            {/*
             // @ts-expect-error TS2322 */}
            <select name="agency_payment_method_id" value={agency_payment_method_id} {...handlers}>
              <option value="">Not Selected</option>
              {filteredAgencyPaymentMethods.map((agencyPaymentMethod) => {
                // @ts-expect-error TS2538
                const paymentMethod = paymentMethods[agencyPaymentMethod.payment_method_id]

                return (
                  <option key={agencyPaymentMethod.id} value={agencyPaymentMethod.id}>
                    {paymentMethod?.title}
                  </option>
                )
              })}
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuote(createOrUpdateCampaignQuotePayload)
              : () => updateCampaignQuote(createOrUpdateCampaignQuotePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Campaign Quote` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
